// Action Center helpers: date resolution, formatting, comparisons, and grouping keys

const riskCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

/**
 * Format a risk dollar value: round to nearest hundred and format as USD currency.
 * @param {number|string} value - Raw risk dollar amount
 * @returns {string} Formatted currency string (e.g. "$1,200")
 */
export function formatRiskDollars(value) {
  const num = Number(value) || 0
  return riskCurrencyFormatter.format(Math.round(num / 100) * 100)
}

/**
 * Parse a contact_emails string (comma or semicolon separated) into an array of valid emails.
 * @param {string} contactEmailsStr - Comma or semicolon separated email string
 * @returns {string[]} Array of valid email addresses
 */
export function parseContactEmails(contactEmailsStr) {
  if (!contactEmailsStr || typeof contactEmailsStr !== 'string') return []
  return contactEmailsStr
    .split(/[,;]+/)
    .map(e => e.trim())
    .filter(e => e && e.includes('@'))
}

/**
 * Resolve full Date for an event's month/day using the plan start date year.
 * If the event's month/day is before the plan start month/day, use next year.
 * If planStartDateStr is invalid, fallback to current-year with rollover.
 * @param {string} monthDayStr - M/D or MM/DD
 * @param {string} planStartDateStr - MM/DD/YYYY
 * @returns {Date}
 */
export function resolveYearForDate(monthDayStr, planStartDateStr) {
  const [monthStr, dayStr] = String(monthDayStr).split('/')
  const eventMonth = parseInt(monthStr, 10)
  const eventDay = parseInt(dayStr, 10)

  if (!planStartDateStr || typeof planStartDateStr !== 'string' || !planStartDateStr.includes('/')) {
    const now = new Date()
    const currentYear = now.getFullYear()
    const candidate = new Date(currentYear, eventMonth - 1, eventDay)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return candidate < today ? new Date(currentYear + 1, eventMonth - 1, eventDay) : candidate
  }

  const [planStartMonthStr, planStartDayStr, planStartYearStr] = planStartDateStr.split('/')
  const planStartMonth = parseInt(planStartMonthStr, 10)
  const planStartDay = parseInt(planStartDayStr, 10)
  const planStartYear = parseInt(planStartYearStr, 10)

  const isBeforePlanStart = (eventMonth < planStartMonth) || (eventMonth === planStartMonth && eventDay < planStartDay)
  const year = isBeforePlanStart ? planStartYear + 1 : planStartYear
  return new Date(year, eventMonth - 1, eventDay)
}

/**
 * Format a Date as long Month Day, Year (e.g., January 5, 2025)
 * @param {Date} dateObj
 * @returns {string}
 */
export function formatMMMDYYYY(dateObj) {
  return dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * Normalize an event's M/D date using the resolved Date object.
 * Handles cases like 2/29 in non-leap years resolving to 3/1.
 * @param {Date} resolvedDate - The resolved full Date
 * @returns {string} Normalized M/D string (e.g., '3/1')
 */
export function normalizeEventDateMD(resolvedDate) {
  return `${resolvedDate.getMonth() + 1}/${resolvedDate.getDate()}`
}

/**
 * Comparable numeric value for Date sorting
 * @param {Date} dateObj
 * @returns {number}
 */
export function toComparable(dateObj) {
  return dateObj.getTime()
}

/**
 * Get start-of-day of a Date object
 * @param {Date} date
 * @returns {Date}
 */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * Check if a Date is in the past compared to today (date-only)
 * @param {Date} date
 * @returns {boolean}
 */
export function isPast(date) {
  const today = startOfDay(new Date())
  const dateOnly = startOfDay(date)
  return dateOnly < today
}

/**
 * Build a stable key for an employer + due-date group
 * @param {object} employerRow - object containing employerId
 * @param {object} group - object containing dueDateObj
 * @returns {string}
 */
export function employerGroupKey(employerRow, group) {
  return `${employerRow.employerId}::${group.dueDateObj.getTime()}`
}

/**
 * Build a stable key for requirement grouping (same body + same M/D date)
 * @param {string} eventBody
 * @param {string} eventMonthDay - M/D or MM/DD
 * @returns {string}
 */
export function requirementGroupKey(eventBody, eventMonthDay) {
  const bodySafe = String(eventBody || '').replace(/\s+/g, ' ').trim()
  const dateSafe = String(eventMonthDay || '').trim()
  return `req::${bodySafe}::${dateSafe}`
}

/**
 * Build a stable key for an employer inside a requirement group
 * Use employerId and plan_year_id to ensure uniqueness across plan years.
 * @param {object} employer - { employerId, plan_year_id }
 * @returns {string}
 */
// requirementEmployerKey removed: selection in wizard uses plan_year_id directly

/**
 * Aggregate a flat list of employer-event items into requirement groups.
 * Ensures consistent keying via requirementGroupKey(eventBody, eventMonthDay)
 * and computes earliestDueDateObj across employers.
 * @param {Array<object>} flatEvents
 * @returns {Array<object>} requirement groups
 */
export function aggregateRequirementsFromFlatEvents(flatEvents) {
  const map = new Map()
  for (const ev of flatEvents || []) {
    const key = requirementGroupKey(ev.eventBody, ev.eventOriginalDateMD)
    if (!map.has(key)) {
      map.set(key, {
        key,
        requirementName: ev.eventName,
        eventBody: ev.eventBody,
        eventMonthDay: ev.eventOriginalDateMD,
        earliestDueDateObj: ev.dueDateObj,
        employers: []
      })
    }
    const group = map.get(key)
    if (ev.dueDateObj && group.earliestDueDateObj && ev.dueDateObj < group.earliestDueDateObj) {
      group.earliestDueDateObj = ev.dueDateObj
    }
    group.employers.push({
      employerId: ev.employerId,
      employerName: ev.employerName,
      contactEmails: ev.contactEmails || '',
      plan_start_date: ev.plan_start_date,
      plan_year_id: ev.plan_year_id,
      dueDateObj: ev.dueDateObj,
      dueDateStr: ev.dueDateStr,
      eventId: ev.eventId,
      eventName: ev.eventName,
      eventOriginalDateMD: ev.eventOriginalDateMD
    })
  }
  return Array.from(map.values())
}

/**
 * Truncate text to a maximum length and add ellipsis if needed
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 30)
 * @returns {string}
 */
export function truncateText(text, maxLength = 30) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Extract all active (non-past) compliance events from an array of plan years.
 * Returns a flat array of event objects with employer and plan year info.
 * @param {Array<object>} planYears - Array of plan year objects with complianceEvents
 * @param {object} options - Optional filters: { dateStart, dateEnd, searchTerm }
 * @returns {Array<object>} Flat array of active events
 */
export function extractActiveEventsFromPlanYears(planYears, options = {}) {
  const { dateStart = null, dateEnd = null, searchTerm = '' } = options
  const start = dateStart ? new Date(dateStart) : null
  const end = dateEnd ? new Date(dateEnd) : null
  const parsedSearchTerm = (searchTerm || '').trim().toLowerCase()

  const flat = []
  for (const planYear of planYears || []) {
    const employerName = planYear.employer?.name || 'Unknown Employer'
    const employerId = planYear.employer?.id || planYear.employer_id
    const rawEvents = Array.isArray(planYear.complianceEvents?.results) ? planYear.complianceEvents.results : []
    
    for (const event of rawEvents) {
      const dueDateObj = resolveYearForDate(event.date, planYear.plan_start_date)
      
      // Hide past-due events
      if (isPast(dueDateObj)) continue
      
      // Apply date range filter if provided
      if (start && dueDateObj < new Date(start.getFullYear(), start.getMonth(), start.getDate())) continue
      if (end && dueDateObj > new Date(end.getFullYear(), end.getMonth(), end.getDate())) continue

      // Apply search filter if provided
      const requirementName = event.name
      if (parsedSearchTerm) {
        const matchesSearch = requirementName?.toLowerCase().includes(parsedSearchTerm)
          || employerName.toLowerCase().includes(parsedSearchTerm)
        if (!matchesSearch) continue
      }

      flat.push({
        employerId,
        employerName,
        contactEmails: planYear.employer?.contact_emails || '',
        plan_start_date: planYear.plan_start_date,
        plan_year_id: planYear.id || planYear.plan_year_id || null,
        dueDateObj,
        dueDateStr: formatMMMDYYYY(dueDateObj),
        eventId: event.id,
        eventName: event.name,
        eventBody: event.body,
        eventOriginalDateMD: normalizeEventDateMD(dueDateObj)
      })
    }
  }
  
  return flat
}
